import axios from 'axios'
import { type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { DniAnalytics } from './Analytics'

export interface KlaviyoConfig {
  publicApiKey?: string
  enable?: boolean
  userId?: string | null
}

export class Klaviyo extends DniAnalytics<AnalyticsEvents> {
  readonly config: KlaviyoConfig
  private static instance: Klaviyo | null = null

  constructor(config: KlaviyoConfig) {
    super()
    this.config = config
    void this.initialize()
  }

  public static getInstance(config: KlaviyoConfig): Klaviyo {
    if (!Klaviyo.instance) {
      Klaviyo.instance = new Klaviyo(config)
    }
    return Klaviyo.instance
  }

  /**
   * 使用 axios 創建或更新 Klaviyo Profile
   */
  private async createOrUpdateProfile(externalId: string): Promise<void> {
    try {
      const url = `https://a.klaviyo.com/client/profiles?company_id=${this.config.publicApiKey}`

      await axios.post(url, {
        data: {
          type: 'profile',
          attributes: { external_id: externalId },
        },
      }, {
        headers: {
          'accept': 'application/vnd.api+json',
          'revision': '2025-07-15',
          'content-type': 'application/vnd.api+json',
        },
      })
    }
    catch (error) {
      console.error('[Klaviyo] Failed to create/update profile:', error)
      throw error
    }
  }

  protected async initialize(): Promise<void> {
    if (this.config.enable === false || !this.config.publicApiKey) {
      this.setIsReady(false)
      return
    }

    try {
      /** 先執行 Client API 創建/更新 Profile */
      if (this.config.userId) {
        await this.createOrUpdateProfile(this.config.userId)
      }

      /** 執行 SDK 初始化流程 */
      this.setIsReady(false)
      this.initKlaviyoObject()
      await this.loadScript()
      await this.onScriptLoaded()
    }
    catch (error) {
      console.error('[Klaviyo] Profile API call failed, continuing with SDK initialization:', error)
      /** 即使 API 失敗，仍繼續 SDK 初始化 */
      this.setIsReady(false)
      this.initKlaviyoObject()
      await this.loadScript()
      await this.onScriptLoaded()
    }
  }

  /**
   * 官方提供的 Klaviyo Object Loader
   * @see https://developers.klaviyo.com/en/docs/introduction_to_the_klaviyo_object
   */
  private initKlaviyoObject(): void {
    if (!window.klaviyo) {
      window._klOnsite = window._klOnsite || []

      try {
        window.klaviyo = new Proxy({}, {
          get: (_, method) => {
            if (method === 'push') {
              return (...args: unknown[]) => {
                window._klOnsite?.push(args)
              }
            }
            return (...args: unknown[]) => {
              const callback = typeof args[args.length - 1] === 'function'
                ? args.pop() as (result: unknown) => void
                : undefined

              return new Promise((resolve) => {
                const wrappedCallback = (result: unknown) => {
                  if (callback) callback(result)
                  resolve(result)
                }
                window._klOnsite?.push([method, ...args, wrappedCallback])
              })
            }
          },
        }) as unknown as typeof window.klaviyo
      }
      catch (error) {
        console.error('Klaviyo initKlaviyoObject error', error)
        window.klaviyo = (window.klaviyo || []) as unknown as typeof window.klaviyo
        if (Array.isArray(window.klaviyo)) {
          (window.klaviyo as unknown as { push: (...args: unknown[]) => void }).push = (...args: unknown[]) => {
            window._klOnsite?.push(args)
          }
        }
      }
    }
  }

  private async loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const scriptId = `klaviyo-${this.config.publicApiKey}`
      const existingScript = document.querySelector(`script[data-id="${scriptId}"]`)

      if (existingScript) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.async = true
      script.type = 'text/javascript'
      script.src = `https://static.klaviyo.com/onsite/js/${this.config.publicApiKey}/klaviyo.js`
      script.setAttribute('data-id', scriptId)

      script.onload = () => resolve()
      script.onerror = () => reject(new Error('[Klaviyo] Failed to load SDK script'))
      document.head.appendChild(script)
    })
  }

  private async checkIfIdentified(): Promise<boolean> {
    if (!window.klaviyo?.isIdentified) {
      return false
    }

    try {
      const result = await window.klaviyo.isIdentified()
      return result
    }
    catch (error) {
      console.warn('[Klaviyo] isIdentified check failed:', error)
      return false
    }
  }

  private sendPageViewEvent(): void {
    this.callKlaviyo('track', 'Viewed Page', {
      page_title: document.title,
      page_location: window.location.href,
    })
  }

  private async onScriptLoaded(): Promise<void> {
    try {
      await this.callKlaviyo('identify', {
        $id: this.config.userId,
      })
      const isIdentified = await this.checkIfIdentified()
      if (!isIdentified) {
        return
      }

      /** 手動發送 Viewed Page Event */
      this.sendPageViewEvent()

      this.setIsReady(true)
    }
    catch (error) {
      console.error('[Klaviyo] onScriptLoaded error', error)
      this.setIsReady(false)
    }
  }

  private async callKlaviyo(method: string, ...args: unknown[]): Promise<void> {
    if (!window.klaviyo) return

    if (typeof window.klaviyo === 'object' && !Array.isArray(window.klaviyo)) {
      switch (method) {
        case 'identify':
          if (typeof window.klaviyo.identify === 'function') {
            await window.klaviyo.identify(args[0] as Record<string, unknown>)
          }
          break
        case 'track':

          if (typeof window.klaviyo.track === 'function') {
            window.klaviyo.track(args[0] as string, args[1] as Record<string, unknown>)
          }
          break
      }
    }
    else if (Array.isArray(window.klaviyo)) {
      window.klaviyo.push([method, ...args])
    }
  }

  protected async sendAnalyticsEvent(customEvent: BaseAnalyticsEvent): Promise<void> {
    if (this.config.enable === false) return

    const { eventType = 'event', eventName, eventParams } = customEvent

    if (eventType === 'event') {
      await this.callKlaviyo('track', eventName, eventParams)
    }
    else return
  }
}
